import { useState, useEffect } from 'react';

declare global {
  interface Window {
    __pwaInstallPrompt: any;
  }
}

export type InstallState =
  | 'not-applicable'
  | 'android-ready'
  | 'ios-manual'
  | 'installed';

function isIOS(): boolean {
  return (
    /iphone|ipad|ipod/i.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
}

function isInStandaloneMode(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as any).standalone === true
  );
}

export function usePWAInstall() {
  const [installState, setInstallState] = useState<InstallState>('not-applicable');

  useEffect(() => {
    if (isInStandaloneMode()) {
      setInstallState('installed');
      return;
    }
    if (isIOS()) {
      setInstallState('ios-manual');
      return;
    }
    if (window.__pwaInstallPrompt) {
      setInstallState('android-ready');
      return;
    }
    const onReady     = () => setInstallState('android-ready');
    const onInstalled = () => setInstallState('installed');
    window.addEventListener('pwaInstallReady', onReady);
    window.addEventListener('pwaInstalled', onInstalled);
    return () => {
      window.removeEventListener('pwaInstallReady', onReady);
      window.removeEventListener('pwaInstalled', onInstalled);
    };
  }, []);

  const triggerInstall = async (): Promise<boolean> => {
    const prompt = window.__pwaInstallPrompt;
    if (!prompt) return false;
    prompt.prompt();
    const { outcome } = await prompt.userChoice;
    if (outcome === 'accepted') {
      window.__pwaInstallPrompt = null;
      setInstallState('installed');
      return true;
    }
    return false;
  };

  const dismiss = () => setInstallState('not-applicable');

  return { installState, triggerInstall, dismiss };
}
